import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProfileCompletion } from '../../services/profileService';
import { isPrestataireLibreRole, isClientRole } from '../../utils/roleUtils';
import { useAcheteurProfile } from '../../contexts/AcheteurProfileContext';
import { useToast } from '../Toast';

// Rappel differe d'1 heure quand l'utilisateur choisit "Me rappeler ulterieurement".
const SNOOZE_MS = 60 * 60 * 1000;

// Id stable : re-emettre ne cree pas de doublon, ca met a jour le meme toast.
const TOAST_ID = 'profil-incomplet';

// ─── Incitation a completer le profil (acheteurs + prestataires libres) ──────
// Rendu sous forme de TOAST persistant (bas a droite), empilable avec les autres.
// Non bloquant, ne disparait pas seul : l'utilisateur doit choisir une action.
// Ce composant ne rend aucun DOM propre — il pilote seulement le toast.
const PopupProfilIncomplet: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { completion: acheteurCompletion } = useAcheteurProfile?.() || { completion: null };
  const [completion, setCompletion] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);

  const isAcheteur = !!user?.role && isClientRole(user.role);
  const isPrestaLibre = !!user?.role && isPrestataireLibreRole(user.role);
  const onProfil = location.pathname === '/profil';
  const snoozeKey = `centralis_profil_popup_snooze_${(user as any)?.id ?? (user as any)?.email ?? ''}`;

  // Recupere la completion selon le role.
  useEffect(() => {
    if (!user) return;
    if (isPrestaLibre) {
      getProfileCompletion()
        .then((data) => setCompletion(data.percent))
        .catch(() => { });
    } else if (isAcheteur && typeof acheteurCompletion?.percent === 'number') {
      setCompletion(acheteurCompletion.percent);
    }
  }, [user, isPrestaLibre, isAcheteur, acheteurCompletion]);

  // Affiche si incomplet et hors page profil ; programme la reapparition a
  // l'expiration du snooze (sans rechargement). Se ré-arme a chaque fermeture.
  useEffect(() => {
    if (visible || onProfil) return;
    if (completion === null || completion >= 100) return;
    const until = Number(localStorage.getItem(snoozeKey) || 0);
    const delay = Math.max(0, until - Date.now());
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [visible, onProfil, completion, snoozeKey]);

  // En arrivant sur la page profil, on masque (le toast sera retire par l'effet ci-dessous).
  useEffect(() => {
    if (onProfil) setVisible(false);
  }, [onProfil]);

  // Emet / retire le toast persistant selon l'etat calcule.
  useEffect(() => {
    const shouldShow = visible && completion !== null && completion < 100 && !onProfil;
    if (!shouldShow) {
      toast.dismiss(TOAST_ID);
      return;
    }
    const snooze = () => localStorage.setItem(snoozeKey, String(Date.now() + SNOOZE_MS));
    toast.show({
      id: TOAST_ID,
      type: 'warning',
      position: 'bottom-right',
      persistent: true,
      dismissible: false, // pas de croix : l'action passe par un des deux boutons
      title: 'Complétez votre profil',
      message: (
        <>
          Votre profil n'est rempli qu'à <strong>{completion}%</strong>. Prochainement, un profil
          complet sera indispensable pour poursuivre l'utilisation de Centralis.
        </>
      ),
      actions: [
        {
          label: 'Compléter mon profil',
          variant: 'primary',
          // On snooze aussi pour ne pas reapparaitre pendant qu'on remplit le profil.
          onClick: () => { snooze(); setVisible(false); navigate('/profil'); },
        },
        {
          label: 'Me rappeler ultérieurement',
          variant: 'secondary',
          onClick: () => { snooze(); setVisible(false); },
        },
      ],
    });
  }, [visible, completion, onProfil, snoozeKey, toast, navigate]);

  // Retire le toast au demontage (logout, changement de layout...).
  useEffect(() => () => { toast.dismiss(TOAST_ID); }, [toast]);

  return null;
};

export default PopupProfilIncomplet;
